import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import { formatter } from '../../../constants';
import { resolveOriginalJobRef } from 'app/main/loadz/components/OriginalJobLabel';
import { fitLogoBox, printPdfDocument, renderPdfBlob } from './invoicePrintShared';

const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 64,
    paddingHorizontal: 34,
    fontSize: 10,
    color: '#111111',
    fontFamily: 'Helvetica',
  },
  printStamp: {
    position: 'absolute',
    top: 16,
    left: 34,
    right: 34,
  },
  printStampText: {
    fontSize: 9,
    color: '#111111',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerLeft: {
    width: '48%',
  },
  headerRight: {
    width: '48%',
    alignItems: 'flex-end',
  },
  logo: {
    height: 70,
    marginBottom: 6,
  },
  companyName: {
    fontSize: 12,
    marginBottom: 2,
  },
  companyAddress: {
    fontSize: 11,
  },
  title: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    marginBottom: 8,
    lineHeight: 1.15,
  },
  meta: {
    fontSize: 12,
    textAlign: 'right',
    marginBottom: 2,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6,
  },
  table: {
    width: '100%',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: '#000000',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  cell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000000',
    paddingVertical: 5,
    paddingHorizontal: 4,
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 9,
    textAlign: 'center',
  },
  bold: {
    fontFamily: 'Helvetica-Bold',
  },
  og: {
    fontSize: 8,
    color: '#9c9c9c',
    textAlign: 'center',
    marginTop: 1,
  },
  footer: {
    position: 'absolute',
    bottom: 22,
    left: 34,
    right: 34,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerBrand: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pageNumber: {
    fontSize: 10,
    color: '#111111',
  },
  footerText: {
    fontSize: 11,
    marginRight: 8,
  },
  footerLogo: {
    width: 110,
    height: 24,
  },
});

const COLUMN_WIDTHS = ['16%', '24%', '20%', '20%', '20%'];

function moneyWithSign(value) {
  const amount = String(value ?? '').replace(/\$/g, '').trim();
  return amount ? `$${amount}` : '';
}

function parseMoney(value) {
  const number = parseFloat(String(value ?? '').replace(/\$|,/g, ''));
  return Number.isFinite(number) ? number : 0;
}

function titleText(printScope) {
  if (printScope === 'open') return 'CUSTOMER\nOPEN INVOICES';
  if (printScope === 'paid') return 'CUSTOMER\nPAID INVOICES';
  if (printScope === 'both') return 'CUSTOMER INVOICES';
  return 'CUSTOMERS INVOICES';
}

function sectionsForScope(printScope, openInvoices, paidInvoices, labels) {
  return [
    printScope !== 'paid'
      ? {
          title: labels.openInvoices,
          rows: openInvoices,
          showTitle: printScope !== 'open',
        }
      : null,
    printScope !== 'open'
      ? {
          title: labels.paidInvoices,
          rows: paidInvoices,
          showTitle: printScope !== 'paid',
        }
      : null,
  ].filter(Boolean);
}

function PdfText({ style, children }) {
  return (
    <Text style={style} hyphenationCallback={(word) => [word]}>
      {children}
    </Text>
  );
}

function Cell({ width, align, bold, children }) {
  return (
    <View style={[styles.cell, { width }]}>
      <PdfText style={[styles.cellText, { textAlign: align || 'center' }, bold ? styles.bold : null]}>
        {children}
      </PdfText>
    </View>
  );
}

function InvoiceTable({ section, labels }) {
  const amountTotal = section.rows.reduce((sum, invoice) => sum + parseMoney(invoice.total), 0);
  const balanceTotal = section.rows.reduce((sum, invoice) => sum + parseMoney(invoice.balance), 0);
  const headers = [labels.invoiceNo, labels.jobNo, labels.dueDate, labels.invoiceAmount, labels.balance];

  return (
    <View style={styles.section}>
      {section.showTitle ? <PdfText style={styles.sectionTitle}>{section.title}</PdfText> : null}
      <View style={styles.table}>
        <View style={styles.row} wrap={false}>
          {headers.map((header, index) => (
            <Cell key={header} width={COLUMN_WIDTHS[index]} bold>
              {header}
            </Cell>
          ))}
        </View>
        {section.rows.length === 0 ? (
          <View style={styles.row} wrap={false}>
            <Cell width='100%'>{labels.empty}</Cell>
          </View>
        ) : (
          section.rows.map((invoice) => {
            const original = resolveOriginalJobRef(invoice);
            const key = `${section.title}-${invoice.invoiceId || invoice.invoice}`;
            return (
              <View key={key} style={styles.row} wrap={false}>
                <Cell width={COLUMN_WIDTHS[0]}>{invoice.invoice}</Cell>
                <View style={[styles.cell, { width: COLUMN_WIDTHS[1] }]}>
                  <PdfText style={styles.cellText}>{invoice.job}</PdfText>
                  {original ? <PdfText style={styles.og}>OG: {original.reference}</PdfText> : null}
                </View>
                <Cell width={COLUMN_WIDTHS[2]}>
                  {invoice.dueDate ? moment(invoice.dueDate).format('MM/DD/YYYY') : ''}
                </Cell>
                <Cell width={COLUMN_WIDTHS[3]} align='right'>
                  {moneyWithSign(invoice.total)}
                </Cell>
                <Cell width={COLUMN_WIDTHS[4]} align='right'>
                  {moneyWithSign(invoice.balance)}
                </Cell>
              </View>
            );
          })
        )}
        <View style={styles.row} wrap={false}>
          <Cell width={COLUMN_WIDTHS[0]} bold>
            {labels.total}
          </Cell>
          <Cell width={COLUMN_WIDTHS[1]} />
          <Cell width={COLUMN_WIDTHS[2]} />
          <Cell width={COLUMN_WIDTHS[3]} align='right' bold>
            {formatter.format(amountTotal)}
          </Cell>
          <Cell width={COLUMN_WIDTHS[4]} align='right' bold>
            {formatter.format(balanceTotal)}
          </Cell>
        </View>
      </View>
    </View>
  );
}

function CustomerInvoicesPrintPdf({
  logoSrc,
  logoWidth,
  logoHeight,
  loadzLogoSrc,
  companyName,
  companyAddress,
  printScope,
  customerName,
  jobLabel,
  jobNoLabel,
  projectLabel,
  projectName,
  openInvoices,
  paidInvoices,
  labels,
  printedAt,
}) {
  const customerLine = [customerName, jobLabel != null && jobLabel !== '' ? `${jobNoLabel} ${jobLabel}` : '']
    .filter(Boolean)
    .join(' · ');
  const sections = sectionsForScope(printScope, openInvoices, paidInvoices, labels);

  return (
    <Document title='Customer Invoices'>
      <Page size='LETTER' style={styles.page}>
        <View style={styles.printStamp} fixed>
          <PdfText style={styles.printStampText}>{printedAt}</PdfText>
        </View>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoSrc ? (
              <Image
                src={logoSrc}
                style={[styles.logo, fitLogoBox(logoWidth, logoHeight, 70, 250)].filter(Boolean)}
              />
            ) : null}
            <PdfText style={styles.companyName}>{companyName}</PdfText>
            {companyAddress ? <PdfText style={styles.companyAddress}>{companyAddress}</PdfText> : null}
          </View>
          <View style={styles.headerRight}>
            <PdfText style={styles.title}>{titleText(printScope)}</PdfText>
            {customerLine ? <PdfText style={styles.meta}>{customerLine}</PdfText> : null}
            <PdfText style={styles.meta}>
              {projectLabel}: {projectName || '-'}
            </PdfText>
          </View>
        </View>
        {sections.map((section) => (
          <InvoiceTable key={section.title} section={section} labels={labels} />
        ))}
        <View style={styles.footer} fixed>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          />
          <View style={styles.footerBrand}>
            <PdfText style={styles.footerText}>{labels.poweredBy}</PdfText>
            {loadzLogoSrc ? <Image src={loadzLogoSrc} style={styles.footerLogo} /> : null}
          </View>
        </View>
      </Page>
    </Document>
  );
}

function customerInvoicesDocument(props) {
  const { companyLogoSrc, previewWindow, ...pdfProps } = props;
  return {
    companyLogoSrc,
    previewWindow,
    createDocument: (assets) => <CustomerInvoicesPrintPdf {...pdfProps} {...assets} />,
  };
}

export async function buildCustomerInvoicesPdfBlob(props) {
  const { companyLogoSrc, createDocument } = customerInvoicesDocument(props);
  return renderPdfBlob({ companyLogoSrc, createDocument });
}

export async function printCustomerInvoicesPdf(props) {
  const { previewWindow, companyLogoSrc, createDocument } = customerInvoicesDocument(props);
  await printPdfDocument({
    previewWindow,
    companyLogoSrc,
    frameId: 'customer-invoices-print-frame',
    frameTitle: 'Customer invoices',
    createDocument,
  });
}
